import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { bookingService, Booking } from '../services/BookingService';
import { API_BASE_URL } from '../config/api';
import QRCode from 'react-qr-code';
import '../styles/AdditionalPaymentSuccess.css';

interface AdditionalTicket {
  ticketNumber: number;
  qrCode: string;
  qrCodeUrl?: string;
  paymentId: string;
  purchaseDate: string;
}

function AdditionalPaymentSuccess() {
  const { bookingId, ticketCount } = useParams<{ bookingId: string; ticketCount: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [additionalTickets, setAdditionalTickets] = useState<AdditionalTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadPurchasedTickets = async () => {
      if (bookingId) {
        try {
          setLoading(true);
          
          // جلب بيانات الحجز
          const bookingData = await bookingService.getBookingById(bookingId);
          if (bookingData) {
            setBooking(bookingData);
          }
          
          // جلب التذاكر الإضافية المشتراة مؤخراً
          const response = await fetch(`${API_BASE_URL}/api/additional-tickets/latest/${bookingId}/${ticketCount}`);
          
          if (response.ok) {
            const data = await response.json();
            setAdditionalTickets(data.tickets);
          } else {
            throw new Error('فشل في جلب التذاكر المشتراة');
          }
        } catch (error: any) {
          console.error('Error loading purchased tickets:', error);
          setError(error.message || 'حدث خطأ في جلب التذاكر');
        } finally {
          setLoading(false);
        }
      }
    };

    loadPurchasedTickets();
  }, [bookingId, ticketCount]);

  return (
    <motion.div 
      className="payment-success-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="success-container">
        <motion.div 
          className="success-icon"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ 
            type: "spring",
            stiffness: 200,
            damping: 10,
            delay: 0.2
          }}
        >
          ✓
        </motion.div>
        
        <h1>شكراً لك!</h1>
        <p className="success-message">
          تم إضافة <strong>{ticketCount}</strong> تذكرة إضافية لحجزك بنجاح
        </p>
        
        {loading ? (
          <div className="loading-message">
            <p>جاري تحميل التذاكر...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p style={{ color: 'red' }}>{error}</p>
          </div>
        ) : additionalTickets.length > 0 ? (
          <div className="qr-section">
            <h2>التذاكر المشتراة</h2>
            <p className="qr-instruction">
              اعرض هذه الرموز عند البوث للدخول مباشرة
            </p>
            
            <div className="tickets-grid">
              {additionalTickets.map((ticket, index) => (
                <div key={index} className="ticket-card">
                  <div className="qr-code-container">
                    {ticket.qrCodeUrl ? (
                      <img 
                        src={ticket.qrCodeUrl} 
                        alt={`QR Code for ticket ${ticket.ticketNumber}`}
                        style={{ width: "150px", height: "150px", borderRadius: "8px" }}
                      />
                    ) : (
                      <QRCode 
                        value={ticket.qrCode}
                        size={150}
                        style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                        viewBox={`0 0 150 150`}
                      />
                    )}
                    <p className="ticket-number">تذكرة رقم {ticket.ticketNumber}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {booking && (
              <div className="booking-details">
                <p><strong>رقم الحجز:</strong> {booking.bookingCode}</p>
                <p><strong>الاسم:</strong> {booking.customerName}</p>
                <p><strong>التاريخ:</strong> {new Date(booking.eventDate).toLocaleDateString('ar-SA')}</p>
                <p><strong>تاريخ الشراء:</strong> {new Date(additionalTickets[0]?.purchaseDate).toLocaleDateString('ar-SA')}</p>
              </div>
            )}

            {/* Remove view all tickets link for security */}
          </div>
        ) : (
          <div className="no-tickets-message">
            <p>لم يتم العثور على تذاكر</p>
          </div>
        )}
        
        <div className="action-buttons">
          {additionalTickets.length > 0 && additionalTickets[0].qrCodeUrl && (
            <button 
              className="primary-btn"
              onClick={() => window.open(additionalTickets[0].qrCodeUrl, '_blank')}
            >
              عرض التذكرة
            </button>
          )}
          <button 
            className="secondary-btn"
            onClick={() => navigate(`/additional-payment/${bookingId}`)}
          >
            اشتر تذاكر إضافية
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default AdditionalPaymentSuccess;